import React, { useState, useEffect } from "react";
import { getRequest, getRequestRow } from "../../apiWrapper";
import { gradeOptions, gradeTabs, filterStudentsFromGrade } from "../../constants/gradeOptions";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const AdminReportDownload = () => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGrade, setSelectedGrade] = useState("all");

  const filteredStudents = filterStudentsFromGrade(students, selectedGrade);

  // 全生徒情報を取得
  useEffect(() => {
    const fetchStudents = async () => {
      setLoading(true);
      try {
        // GET /api/students で全生徒情報を取得
        const data = await getRequest("students");
        console.log("download students ", data.students);
        if (data && data.students) {
          setStudents(data.students);
        }
      } catch (error) {
        console.error("❌ 生徒データの取得エラー:", error);
      }
      setLoading(false);
    };

    fetchStudents();
  }, []);

  // PDFダウンロード処理
  const generateReport = async (studentid) => {
    try {
      // 未確認報告書のチェック
      const checkUrl = `lessons/unconfirmed?studentid=${studentid}`;
      const checkData = await getRequest(checkUrl);
      if (
        checkData.unconfirmedCount &&
        parseInt(checkData.unconfirmedCount, 10) > 0
      ) {
        const proceed = window.confirm(
          "⚠️ この生徒には未確認の報告書があります。\n" +
          "なお、ダウンロードされるPDFには未確認の報告書は含まれません。\n" +
          "それでもダウンロードを続行しますか？"
        );
        if (!proceed) return;
      }

      // PDF生成（既存のクラウド関数等を利用）
      const pdfUrl = `generateLessonReportPDF?studentid=${studentid}`;
      const response = await getRequestRow(pdfUrl);
      if (!response.ok) {
        if (response.status === 404) {
          toast.warn("授業報告が見つかりませんでした");
        } else {
          toast.warn(data.error);
        }
        throw new Error(`PDF生成に失敗しました: ${data.error}`);
      }
      // 📌 Content-Disposition ヘッダーからファイル名を取得
      const contentDisposition = response.headers.get("Content-Disposition");
      let fileName = "LessonReport.pdf"; // デフォルトのファイル名

      if (contentDisposition) {
        const match = contentDisposition.match(/filename\*=UTF-8''(.+)/);
        if (match && match[1]) {
          fileName = decodeURIComponent(match[1]); // URLエンコードをデコード
        } else {
          console.warn("ファイル名が Content-Disposition から取得できませんでした");
        }
      } else {
        console.warn("Content-Disposition ヘッダーがありません");
      }

      // 📌 PDFデータを Blob 形式で取得
      const blob = await response.blob();
      const fileURL = window.URL.createObjectURL(blob);

      // 📌 ダウンロードリンクを作成
      const link = document.createElement("a");
      link.href = fileURL;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(fileURL);
    } catch (error) {
      console.error("PDF生成エラー:", error);
    }
  };

  // 現役生と退会生徒に分ける
  const activeStudents = filteredStudents.filter((student) => !student.withdrawn);
  const withdrawnStudents = filteredStudents.filter((student) => student.withdrawn);

  return (
    <div className="p-3 pt-20">
      <h2 className="text-xl font-bold mb-4">📥 生徒の報告書PDFをダウンロード</h2>
      {/* 学年フィルタ（PC: タブ / モバイル: ドロップダウン） */}
      <div className="mb-4">
        <div className="hidden md:flex space-x-2 overflow-x-auto pb-2">
          {gradeTabs.map((opt) => (
            <button
              key={opt.value}
              className={`px-4 py-2 rounded-lg transition ${selectedGrade === opt.value ? "bg-blue-500 text-white" : "bg-gray-200"}`}
              onClick={() => setSelectedGrade(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <div className="md:hidden">
          <select
            value={selectedGrade}
            onChange={(e) => setSelectedGrade(e.target.value)}
            className="w-full p-2 border rounded"
          >
            {gradeOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      </div>
      {loading ? (
        <p>データを取得中...</p>
      ) : (
        <div className="space-y-8">
          {/* 現役生一覧 */}
          <div>
            <h3 className="text-lg font-semibold mb-2">現役生一覧</h3>
            {activeStudents.length > 0 ? (
              <ul className="space-y-2">
                {activeStudents.map((student) => (
                  <li
                    key={student.id}
                    className="flex justify-between items-center p-3 border rounded-lg"
                  >
                    <span>{student.name}</span>
                    <button
                      onClick={() => generateReport(student.id)}
                      className="bg-blue-500 text-white px-3 py-1 rounded-lg"
                    >
                      ダウンロード
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p>現役生は登録されていません</p>
            )}
          </div>

          {/* 退会生徒一覧 */}
          <div>
            <h3 className="text-lg font-semibold mb-2">退会生徒一覧</h3>
            {withdrawnStudents.length > 0 ? (
              <ul className="space-y-2">
                {withdrawnStudents.map((student) => (
                  <li
                    key={student.id}
                    className="flex justify-between items-center p-3 border rounded-lg"
                  >
                    <span>{student.name}</span>
                    <button
                      onClick={() => generateReport(student.id)}
                      className="bg-blue-500 text-white px-3 py-1 rounded-lg"
                    >
                      ダウンロード
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p>退会生徒は登録されていません</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminReportDownload;
